import { HTTP } from "@/core/http";
import type {
  BusinessRequirement,
  BusinessRequirementApiRow,
  BusinessRequirementGenerationApiResponse,
  GenerateBusinessRequirementsParams,
  GenerateBusinessRequirementsResult,
  BusinessRequirementFilters,
  BusinessRequirementsMetadata,
  BusinessRequirementHistory,
  BusinessRequirementMetadataOptionItem,
  BusinessRequirementMetadataValue,
} from "../types";
import {
  getBusinessRequirementEnum,
  BUSINESS_REQUIREMENT_SOURCE_USER,
  BUSINESS_REQUIREMENT_STATUS_DRAFT,
} from "../utils/businessRequirementMetadataDisplay";

const BASE = "/pms/business-requirements";

type ApiRow = Partial<BusinessRequirementApiRow> & Record<string, unknown>;

function text(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

/** HTTP client for Business Requirements (`/pms/business-requirements/...`). */
export class BusinessRequirementsService {
  constructor(private readonly httpClient: HTTP) {}

  private asMetadata(raw: unknown): BusinessRequirementMetadataValue {
    if (raw == null || raw === "") return undefined;
    if (typeof raw === "string") return raw;
    if (typeof raw === "object") {
      const o = raw as { enum?: unknown; label?: unknown };
      if (typeof o.enum === "string" && typeof o.label === "string") {
        return { enum: o.enum, label: o.label };
      }
      if (typeof o.enum === "string") {
        return { enum: o.enum, label: o.enum };
      }
    }
    return undefined;
  }

  /** Map PMS `BusReqResponse` → UI model. */
  fromApiBusinessRequirement(raw: ApiRow): BusinessRequirement {
    const busReqId = Number(raw.busReqId);
    const projectId = Number(raw.projectId);
    const source = this.asMetadata(raw.busReqSource);
    const status = this.asMetadata(raw.busReqStatus);
    const remarks = text(raw.busReqRemarks);
    const updatedAt = text(raw.busReqUpdatedAt);
    const updatedBy = raw.busReqUpdatedBy ?? undefined;

    return {
      busReqId: Number.isFinite(busReqId) ? busReqId : undefined,
      reqId: Number.isFinite(busReqId) ? busReqId : undefined,
      projectId: Number.isFinite(projectId) ? projectId : undefined,
      busReqModule: text(raw.busReqModule),
      reqTitle: text(raw.busReqTitle),
      requirementDescription: text(raw.busReqDescription),
      reqComplexity: this.asMetadata(raw.busReqComplexity),
      busReqEstEffort: raw.busReqEstEffort ?? null,
      busReqFinalEffort: raw.busReqFinalEffort ?? null,
      busReqEffortRationale: text(raw.busReqEffortRationale) ?? null,
      reqOriginDocType: this.asMetadata(raw.busReqOriginDocType),
      reqOriginDocName: text(raw.busReqOriginDocName),
      reqOriginLocation: text(raw.busReqOriginLocation),
      reqSource: source,
      requirementsSource: source,
      reqStatus: status,
      requirementsStatus: status,
      reqRemark: remarks,
      reqRemarks: remarks,
      busReqUpdatedAt: updatedAt,
      updatedAt,
      busReqUpdatedBy: updatedBy,
      updatedBy,
    };
  }

  /** PMS `BusReqRequest`; enums are sent as plain strings. */
  private toApiPayload(
    data: Partial<BusinessRequirement> & { projectId?: number },
  ): Record<string, unknown> {
    const payload: Record<string, unknown> = {
      projectId: data.projectId,
      busReqModule: data.busReqModule ?? "",
      busReqTitle: data.reqTitle ?? "",
      busReqDescription: data.requirementDescription ?? "",
      busReqComplexity:
        getBusinessRequirementEnum(data.reqComplexity) || undefined,
      busReqOriginDocType:
        getBusinessRequirementEnum(data.reqOriginDocType) || undefined,
      busReqOriginLocation: data.reqOriginLocation ?? "",
      busReqSource:
        getBusinessRequirementEnum(data.reqSource ?? data.requirementsSource) ||
        BUSINESS_REQUIREMENT_SOURCE_USER,
      busReqStatus:
        getBusinessRequirementEnum(data.reqStatus ?? data.requirementsStatus) ||
        BUSINESS_REQUIREMENT_STATUS_DRAFT,
      busReqRemarks: data.reqRemarks ?? data.reqRemark ?? "",
    };

    if (data.reqOriginDocName !== undefined) {
      payload.busReqOriginDocName = data.reqOriginDocName;
    }
    if (data.busReqEstEffort != null) {
      payload.busReqEstEffort = data.busReqEstEffort;
    }
    if (data.busReqFinalEffort != null) {
      payload.busReqFinalEffort = data.busReqFinalEffort;
    }
    if (data.busReqEffortRationale != null) {
      payload.busReqEffortRationale = data.busReqEffortRationale;
    }

    return payload;
  }

  /**
   * POST /pms/business-requirements/generate/{projectId}?moduleName=... PMS sends the
   * module's RFP Analysis to AI Brain, which saves the requirements as AI / Draft.
   */
  async generateBusinessRequirements(
    params: GenerateBusinessRequirementsParams,
  ): Promise<GenerateBusinessRequirementsResult> {
    const moduleName = params.moduleName.trim();
    if (!moduleName) {
      throw new Error("Select a module to generate requirements for.");
    }
    const raw = await this.httpClient.post<
      Partial<BusinessRequirementGenerationApiResponse>
    >(`${BASE}/generate/${params.projectId}`, {
      params: { moduleName },
    });
    if (!raw?.data) {
      throw new Error(
        "Business requirement generation did not complete. Please try again.",
      );
    }

    const generated = raw.data.business_requirements?.length ?? 0;
    const save = raw.data.pms_save;
    if (save && save.success === false) {
      const errors = (save.errors ?? []).map(String).filter(Boolean);
      throw new Error(
        `Requirements were generated but could not be saved: ${errors.join("; ") || "unknown error"}`,
      );
    }
    return { generated, saved: save?.saved_count ?? 0 };
  }

  async getBusinessRequirements(
    filters: BusinessRequirementFilters,
  ): Promise<BusinessRequirement[]> {
    const params: Record<string, string> = {};
    if (filters.module) params.busReqModule = filters.module;
    if (filters.status) params.busReqStatus = filters.status;
    if (filters.source) params.busReqSource = filters.source;

    const raw = await this.httpClient.get<ApiRow[]>(
      `${BASE}/project/${filters.projectId}`,
      { params },
    );
    return raw.map((r) => this.fromApiBusinessRequirement(r));
  }

  async getBusinessRequirementsMetadata(): Promise<BusinessRequirementsMetadata> {
    const raw = await this.httpClient.get<Record<string, unknown>>(
      `${BASE}/metadata`,
    );
    return this.normalizeBusinessRequirementsMetadata(raw);
  }

  private normalizeBusinessRequirementsMetadata(
    raw: Record<string, unknown>,
  ): BusinessRequirementsMetadata {
    const optionList = (key: string): BusinessRequirementMetadataOptionItem[] => {
      const v = raw[key];
      if (!Array.isArray(v)) return [];
      return v
        .map((item) => {
          if (item == null || typeof item !== "object") return null;
          const o = item as { enum?: unknown; label?: unknown };
          if (typeof o.enum !== "string") return null;
          const label =
            typeof o.label === "string" && o.label.length > 0
              ? o.label
              : o.enum;
          return { enum: o.enum, label };
        })
        .filter((x): x is BusinessRequirementMetadataOptionItem => x != null);
    };
    return {
      complexity: optionList("complexity"),
      source: optionList("source"),
      status: optionList("status"),
      originDocType:
        optionList("originDocType").length > 0
          ? optionList("originDocType")
          : optionList("origin_doc_type"),
      technologyStack:
        optionList("technologyStack").length > 0
          ? optionList("technologyStack")
          : optionList("technology_stack"),
      domain: optionList("domain"),
    };
  }

  async updateBusinessRequirement(
    reqId: number,
    data: Partial<BusinessRequirement>,
  ): Promise<BusinessRequirement> {
    const raw = await this.httpClient.put<ApiRow>(`${BASE}/${reqId}`, {
      body: this.toApiPayload(data),
    });
    return this.fromApiBusinessRequirement(raw);
  }

  /** POST takes and returns an array. */
  async createBusinessRequirement(
    data: Partial<BusinessRequirement> & { projectId: number },
  ): Promise<BusinessRequirement> {
    const raw = await this.httpClient.post<unknown>(BASE, {
      body: [this.toApiPayload(data)],
    });

    const item = Array.isArray(raw) ? raw[0] : raw;
    if (item != null && typeof item === "object" && !Array.isArray(item)) {
      return this.fromApiBusinessRequirement(item as ApiRow);
    }

    return {
      projectId: data.projectId,
      busReqModule: data.busReqModule,
      reqTitle: data.reqTitle,
      requirementDescription: data.requirementDescription,
      reqComplexity: data.reqComplexity,
      reqOriginDocType: data.reqOriginDocType,
      reqOriginLocation: data.reqOriginLocation,
      reqRemarks: data.reqRemarks ?? data.reqRemark,
      reqSource: data.reqSource,
      reqStatus: data.reqStatus,
    };
  }

  async getBusinessRequirementHistory(
    reqId: number,
  ): Promise<BusinessRequirementHistory[]> {
    return this.httpClient.get<BusinessRequirementHistory[]>(
      `${BASE}/${reqId}/history`,
    );
  }
}
